import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { playerRepository } from '../../data/playerRepository';
import { empresaService } from '@/shared/services/empresaService';
import { useOnlineStatus } from '@/shared/hooks/useOnlineStatus';
import type { Tela, Midia } from '@/shared/types';

export interface UsePlayerInfraReturn {
  tela:     Tela | null;
  midias:   Midia[];
  loading:  boolean;
  error:    string;
  revogado: boolean;
  isOnline: boolean;
  setTela(t: Tela): void;
  setMidias: React.Dispatch<React.SetStateAction<Midia[]>>;
  setRevogado(v: boolean): void;
  /** Catch-up de tela e mídias (db_change é broadcast efêmero — sem replay). */
  recarregarTelaEMidias(): void;
}

/** Carga inicial da tela/mídias, estado de licença revogada/erro/loading, e
 *  reconexão automática (re-busca mídias) após queda de rede. */
export function usePlayerInfra(telaId: string | undefined): UsePlayerInfraReturn {
  const navigate = useNavigate();
  const [tela,     setTela]     = useState<Tela | null>(null);
  const [midias,   setMidias]   = useState<Midia[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [revogado, setRevogado] = useState(false);
  const telaIdAtualRef = useRef(telaId);
  const versaoCargaRef = useRef(0);
  telaIdAtualRef.current = telaId;

  const cargaAindaAtual = (id: string, versao: number) => (
    telaIdAtualRef.current === id && versaoCargaRef.current === versao
  );

  useEffect(() => {
    if (!telaId) return;
    let cancelado = false;
    const versao = ++versaoCargaRef.current;
    setTela(null);
    setMidias([]);
    setRevogado(false);
    setError('');
    setLoading(true);
    (async () => {
      try {
        const [telaData, midiasData, empresaStatus] = await Promise.all([
          playerRepository.buscarPorId(telaId),
          playerRepository.listarMidias(telaId),
          empresaService.buscarStatusPorTela(telaId),
        ]);
        if (cancelado || !cargaAindaAtual(telaId, versao)) return;
        if (empresaStatus === 'suspenso') {
          navigate('/bloqueado', { replace: true });
          return;
        }
        if (telaData.revogada) setRevogado(true);
        setTela(telaData);
        setMidias(midiasData);
      } catch (err) {
        if (!cancelado && cargaAindaAtual(telaId, versao)) setError(err instanceof Error ? err.message : 'Tela não encontrada');
      } finally {
        if (!cancelado && cargaAindaAtual(telaId, versao)) setLoading(false);
      }
    })();
    return () => { cancelado = true; versaoCargaRef.current += 1; };
  }, [telaId, navigate]);

  const isOnline = useOnlineStatus();
  const wasOfflineRef = useRef(false);
  useEffect(() => { if (!isOnline) wasOfflineRef.current = true; }, [isOnline]);
  useEffect(() => {
    if (!isOnline || !wasOfflineRef.current || !telaId || loading) return;
    wasOfflineRef.current = false;
    let cancelado = false;
    const versao = ++versaoCargaRef.current;
    playerRepository.listarMidias(telaId).then(data => {
      if (!cancelado && cargaAindaAtual(telaId, versao)) setMidias(data);
    }).catch(() => {});
    return () => { cancelado = true; };
  }, [isOnline, telaId, loading]);

  const recarregarTelaEMidias = useCallback(() => {
    if (!telaId) return;
    const versao = ++versaoCargaRef.current;
    Promise.all([
      playerRepository.buscarPorId(telaId),
      playerRepository.listarMidias(telaId),
    ]).then(([t, data]) => {
      if (!cargaAindaAtual(telaId, versao)) return;
      if (t.revogada) setRevogado(true);
      setTela(t);
      setMidias(data);
    }).catch(() => {}).finally(() => {
      if (cargaAindaAtual(telaId, versao)) setLoading(false);
    });
  }, [telaId]);

  return { tela, midias, loading, error, revogado, isOnline, setTela, setMidias, setRevogado, recarregarTelaEMidias };
}
